/**
 * Deterministic Normalization Preview (Processing Pipeline UX redesign)
 *
 * A pure, dependency-free, client-side mirror of the backend deterministic
 * transform engine in `azure-functions/src/services/fieldNormalizationService.ts`
 * (`normalizeValue`). It lets the Normalize editor render before/after previews
 * and live regex-row previews WITHOUT any network round-trip or persistence —
 * the client-side equivalent of `analyzerService.testNormalization()` for preview
 * only. The authoritative transform still runs server-side post-extraction; this
 * file must mirror it EXACTLY in transform order and semantics.
 *
 * No `process.env`, no credentials, no network, no AI/connector calls — pure logic.
 *
 * Transform order (identical to backend `normalizeValue`):
 *   0. No-op contract: no `normalize` AND no `textFormatting` AND no `format`
 *      => return the original value untouched (not even coerced).
 *   1. Coerce to string (null/undefined => '').
 *   2. If `normalize` present:
 *        a. stripWords  b. removeDots  c. removePunctuation
 *        d. regexReplace[] (in order; invalid pattern = non-fatal passthrough)
 *        e. valueMap (trimmed key)  f. dateReformat
 *   3. If `textFormatting` present: trimTrailing[] then case.
 *   4. If `format` present: applyFormat.
 *   5. If `normalize` present (AFTER format): padLeft then truncate.
 */

import type { FieldNormalization, FieldConfiguration } from '../services/analyzerService';

/**
 * The subset of field metadata the normalizer reads — identical shape to the
 * `fieldMeta` argument of `analyzerService.testNormalization()`.
 */
export interface NormalizableFieldMeta {
  normalize?: FieldNormalization;
  textFormatting?: FieldConfiguration['textFormatting'];
  format?: string;
}

/** A single regexReplace rule as stored on `FieldNormalization.regexReplace`. */
export type RegexReplaceRule = { pattern: string; replacement: string; flags?: string };

// ── Regex validity predicate (pure RE2 mirror) ────────────────────────────────

/**
 * RE2-incompatible constructs that the processing engine (RE2 via
 * `re2-wasm`'s `compileRegexReplace`) rejects but native JS `RegExp` accepts:
 * lookahead, negative lookahead, lookbehind, negative lookbehind, and
 * backreferences `\1`..`\9`. RE2 is a finite-automaton engine: it has no
 * lookaround and no backreferences.
 */
const RE2_LOOKAROUND = /\(\?<?[=!]/; // matches (?=  (?!  (?<=  (?<!
const RE2_BACKREFERENCE = /\\[1-9]/; // matches \1 .. \9

/**
 * Pure, synchronous predicate that mirrors RE2's acceptance for the realistic
 * config-author surface. A pattern is valid iff:
 *   (a) it compiles under native JS `RegExp` (with the engine's implicit `u`
 *       flag, matching backend `compileRegexReplace` which always adds `u`), AND
 *   (b) it uses no RE2-incompatible construct — lookaround `(?=`, `(?!`, `(?<=`,
 *       `(?<!`, or a backreference `\1`..`\9`.
 *
 * `re2-wasm` is a BACKEND-ONLY dependency and is intentionally NOT bundled into
 * the frontend; this predicate reproduces RE2's relevant acceptance rules in
 * pure JS so it can gate the live preview deterministically (Req 2.9).
 *
 * NOTE: this is a preview-time gate only. Authoritative, save-time validation
 * remains server-side via `analyzerService.validateRegexRules()` (real RE2).
 */
/**
 * Mirror `compileRegexReplace`: RE2 always runs in Unicode mode; the JS API
 * gate requires the 'u' flag explicitly, so add it if absent.
 */
function normalizeFlags(flags?: string): string {
  return flags?.includes('u') ? flags : `${flags ?? ''}u`;
}

export function isValidRegexPattern(pattern: string, flags?: string): boolean {
  if (RE2_LOOKAROUND.test(pattern)) return false;
  if (RE2_BACKREFERENCE.test(pattern)) return false;
  const normalizedFlags = normalizeFlags(flags);
  try {
    // eslint-disable-next-line no-new
    new RegExp(pattern, normalizedFlags);
    return true;
  } catch {
    return false;
  }
}

// ── Helper functions (faithful mirrors of fieldNormalizationService.ts) ────────

/** Remove whole words (case-insensitive) and collapse the whitespace they leave behind. */
function stripWords(value: string, words: string[]): string {
  let out = value;
  for (const w of words) {
    if (!w) continue;
    const escaped = w.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    out = out.replace(new RegExp(`\\b${escaped}\\b`, 'gi'), '');
  }
  return out.replace(/\s{2,}/g, ' ').trim();
}

/** Format a date value using YYYY/YY/MM/DD tokens. */
function formatDateTokens(yyyy: string, mm: string, dd: string, to: string): string {
  return to
    .replace(/YYYY/g, yyyy)
    .replace(/YY/g, yyyy.slice(-2))
    .replace(/MM/g, mm)
    .replace(/DD/g, dd);
}

/**
 * Reformat a date. When `from` is supplied it is parsed deterministically by
 * token position; otherwise falls back to `new Date(value)`. Unparseable input
 * passes through unchanged.
 */
function reformatDate(value: string, spec: { from?: string; to: string }): string {
  if (spec.from) {
    const order: Array<'y' | 'm' | 'd'> = [];
    const pattern = spec.from
      .replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
      .replace(/YYYY|MM|DD/g, (tok) => {
        if (tok === 'YYYY') { order.push('y'); return '(\\d{4})'; }
        if (tok === 'MM') { order.push('m'); return '(\\d{1,2})'; }
        order.push('d'); return '(\\d{1,2})';
      });
    try {
      const m = new RegExp(`^${pattern}$`).exec(value.trim());
      if (m) {
        let yyyy = '', mm = '', dd = '';
        order.forEach((tok, i) => {
          const g = m[i + 1];
          if (tok === 'y') yyyy = g;
          else if (tok === 'm') mm = g.padStart(2, '0');
          else dd = g.padStart(2, '0');
        });
        if (yyyy && mm && dd) return formatDateTokens(yyyy, mm, dd, spec.to);
      }
    } catch {
      // fall through to passthrough
    }
    return value;
  }

  const d = new Date(value);
  if (isNaN(d.getTime())) return value;
  return formatDateTokens(
    String(d.getFullYear()),
    String(d.getMonth() + 1).padStart(2, '0'),
    String(d.getDate()).padStart(2, '0'),
    spec.to
  );
}

/** Optional value transform (mirrors backend `applyTransform`); `textFormatting.case` is a subset. */
function applyTransform(value: string, transform?: FieldConfiguration['exportTransform']): string {
  switch (transform) {
    case 'uppercase': return value.toUpperCase();
    case 'lowercase': return value.toLowerCase();
    case 'trim': return value.trim();
    case 'titlecase':
      return value.replace(/\w\S*/g, (w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase());
    default: return value;
  }
}

/** Best-effort output formatting from the field's display/format hint (mirrors backend `applyFormat`). */
export function applyFormat(value: string, format?: string): string {
  if (!format || !value) return value;

  // Currency hint
  if (/[$]|0\.00|#,##0/.test(format)) {
    const n = Number(String(value).replace(/[^0-9.\-]/g, ''));
    if (!isNaN(n)) {
      const formatted = n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      return format.includes('$') ? `$${formatted}` : formatted;
    }
    return value;
  }

  // Date hint (contains YYYY / MM / DD)
  if (/Y{2,4}|MM|DD/.test(format)) {
    const d = new Date(value);
    if (!isNaN(d.getTime())) {
      return formatDateTokens(
        String(d.getFullYear()),
        String(d.getMonth() + 1).padStart(2, '0'),
        String(d.getDate()).padStart(2, '0'),
        format
      );
    }
    return value;
  }

  return value;
}

/**
 * Apply a single regexReplace rule to a string, mirroring the backend's
 * try/catch behavior: an invalid pattern (per `isValidRegexPattern`) is a
 * NON-FATAL passthrough — the rule contributes no replacement.
 */
function applyRegexRule(s: string, rule: RegexReplaceRule): string {
  if (!isValidRegexPattern(rule.pattern, rule.flags)) return s;
  const normalizedFlags = normalizeFlags(rule.flags);
  try {
    return s.replace(new RegExp(rule.pattern, normalizedFlags), rule.replacement);
  } catch {
    return s;
  }
}

// ── Core transform (mirror of backend `normalizeValue`) ────────────────────────

/**
 * Apply the full declarative deterministic transform to a single value, in the
 * exact order the backend `normalizeValue` uses. This is the client-side mirror
 * of `analyzerService.testNormalization()` for preview only — no persistence.
 *
 * No-op contract: with no `normalize`, no `textFormatting`, and no `format`, the
 * original value is returned untouched (not even coerced).
 */
export function applyNormalizePreview(value: unknown, fieldMeta?: NormalizableFieldMeta): unknown {
  const norm = fieldMeta?.normalize;
  const textFormatting = fieldMeta?.textFormatting;
  const format = fieldMeta?.format;

  const hasConfig = !!norm || !!textFormatting || !!format;
  if (!hasConfig) return value;

  let s = value === undefined || value === null ? '' : String(value);

  if (norm) {
    if (norm.stripWords?.length) s = stripWords(s, norm.stripWords);
    if (norm.removeDots) s = s.replace(/\./g, '');
    if (norm.removePunctuation) s = s.replace(/[^\p{L}\p{N}\s]/gu, '');

    if (norm.regexReplace?.length) {
      for (const r of norm.regexReplace) {
        s = applyRegexRule(s, r);
      }
    }

    if (norm.valueMap) {
      const key = s.trim();
      if (Object.prototype.hasOwnProperty.call(norm.valueMap, key)) {
        s = norm.valueMap[key];
      }
    }

    if (norm.dateReformat) s = reformatDate(s, norm.dateReformat);
  }

  if (textFormatting) {
    if (textFormatting.trimTrailing?.length) {
      for (const t of textFormatting.trimTrailing) {
        if (!t) continue;
        while (s.endsWith(t)) s = s.slice(0, s.length - t.length);
      }
    }
    s = applyTransform(s, textFormatting.case);
  }

  if (format) s = applyFormat(s, format);

  if (norm) {
    if (norm.padLeft) s = s.padStart(norm.padLeft.length, norm.padLeft.char || '0');
    if (norm.truncate) s = s.slice(0, norm.truncate.length);
  }

  return s;
}

// ── Before/after example (Req 2.5, 2.6: "Input: [value] → Output: [value]") ────

/** A before/after preview pair for a sample value under a given config. */
export interface NormalizeExample {
  before: string;
  after: string;
}

/**
 * Produce a before/after example for a sample value under a normalization config.
 * `before` is the coerced sample string; `after` is the transform output coerced
 * to string. Drives the "Input: [value] → Output: [value]" display (Req 2.5/2.6).
 */
export function buildNormalizeExample(
  sampleValue: unknown,
  fieldMeta?: NormalizableFieldMeta
): NormalizeExample {
  const before = sampleValue === undefined || sampleValue === null ? '' : String(sampleValue);
  const out = applyNormalizePreview(sampleValue, fieldMeta);
  const after = out === undefined || out === null ? '' : String(out);
  return { before, after };
}

// ── Live per-row regex preview (Req 2.9: validity gates the preview) ───────────

/**
 * The live-preview state for a single regex-replacement row. When the row's
 * pattern is invalid the preview is suppressed and an inline error is surfaced;
 * the row then contributes no replacement to the overall preview.
 */
export interface RegexRowPreview {
  /** True when the pattern is acceptable to the processing engine (RE2 mirror). */
  valid: boolean;
  /** True when the live preview for this row is suppressed (i.e. pattern invalid). */
  suppressed: boolean;
  /** Inline error message shown when the pattern is invalid; undefined when valid. */
  error?: string;
  /** The transformed sample value when valid; undefined when suppressed. */
  after?: string;
}

/** Inline error copy shown when a regex row's pattern is invalid (Req 2.9). */
export const INVALID_REGEX_MESSAGE = 'Invalid regular expression pattern';

/**
 * Compute the live preview for one regex-replacement row against a sample value.
 * Validity gates the preview: a valid pattern yields `after` (the replacement
 * applied); an invalid pattern suppresses the preview, surfaces an inline error,
 * and contributes no replacement (`after` is undefined).
 */
export function previewRegexRow(sampleValue: string, rule: RegexReplaceRule): RegexRowPreview {
  if (!isValidRegexPattern(rule.pattern, rule.flags)) {
    return { valid: false, suppressed: true, error: INVALID_REGEX_MESSAGE };
  }
  return { valid: true, suppressed: false, after: applyRegexRule(sampleValue, rule) };
}

// ── Transformation presets (Req 2.4) ──────────────────────────────────────────

/** The one-click Transformation_Presets offered by the Normalize editor (Req 2.4). */
export type TransformationPreset =
  | 'UPPERCASE'
  | 'lowercase'
  | 'Title Case'
  | 'TRIM'
  | 'Remove Punctuation';

/** The exact ordered set of preset names exposed by the editor. */
export const TRANSFORMATION_PRESETS: readonly TransformationPreset[] = [
  'UPPERCASE',
  'lowercase',
  'Title Case',
  'TRIM',
  'Remove Punctuation',
] as const;

/**
 * Resolve a named Transformation_Preset to its deterministic configuration.
 * Each preset maps to fields that flow through `applyNormalizePreview` unchanged:
 *   - UPPERCASE / lowercase / Title Case => `textFormatting.case`
 *   - TRIM                               => a leading/trailing whitespace regexReplace
 *   - Remove Punctuation                 => `normalize.removePunctuation`
 *
 * Returns a fresh config object each call so callers can safely merge/mutate.
 */
export function resolvePreset(preset: TransformationPreset): NormalizableFieldMeta {
  switch (preset) {
    case 'UPPERCASE':
      return { textFormatting: { case: 'uppercase' } };
    case 'lowercase':
      return { textFormatting: { case: 'lowercase' } };
    case 'Title Case':
      return { textFormatting: { case: 'titlecase' } };
    case 'TRIM':
      return { normalize: { regexReplace: [{ pattern: '^\\s+|\\s+$', replacement: '', flags: 'g' }] } };
    case 'Remove Punctuation':
      return { normalize: { removePunctuation: true } };
    default: {
      // Exhaustiveness guard: unreachable for the declared preset union.
      const _exhaustive: never = preset;
      return _exhaustive;
    }
  }
}
